import * as React from 'react';
import {weakMemo} from '@packages/utilities/memo';
import {map as mapValues} from '@packages/utilities/key-map';
import {TypeImplementation, Expressions} from '@packages/systems/wfdl';
import {useSharedFlowContext} from '@packages/systems/flows/shared';
import {
  getCollectionNameFromVariable,
  selectCMSCollections,
  initializeField,
  supportedFieldsBySlug,
} from '../utils';
import {
  TTypeReference,
  EHole,
  ERecord,
  ECall,
} from '@packages/systems/wfdl-ast';
import {
  ExpressionEditor,
  forwardDispatch,
  updateExpressionAtAction,
} from '@packages/systems/ExpressionEditor-utils';
import {CollectionSettings} from '../components/CollectionSettings';
import {TEffect} from '@packages/systems/plugins/Effect';
import {a} from '@packages/utilities/wfdl-helpers';
import {confirm} from '@packages/systems/dialog';
import {useDesignerSelector} from '@packages/systems/designer/flux';

import {
  ContextualExpressions,
  stripAll,
  stripAnn,
} from '@packages/utilities/contextual-expression';

const error = TTypeReference('error');
const itemCreatorsType = TEffect(error, a);

const atArg = {in: 'Call', at: 'arg'} as const;
const dispatchAtArg = weakMemo(
  (dispatch: TypeImplementation.ExpressionEditorDispatch) =>
    forwardDispatch(dispatch)(atArg)
);

export const ItemCreators = TypeImplementation.Alias({
  params: ['error', 'a'],
  system: {bindUIVariant: 'custom'},
  type: itemCreatorsType,
  render: function ItemCreators({expr, dispatch}) {
    const {useCMSBlock} = useSharedFlowContext();
    const {hasDependencies} = useCMSBlock();
    const collections = useDesignerSelector(selectCMSCollections);
    const call = ContextualExpressions.getCall(stripAnn(expr));
    const name = call
      ? getCollectionNameFromVariable(stripAll(call.fun))
      : null;

    const onCollectionChange = React.useCallback(
      // @ts-expect-error - TS7006 - Parameter 'collectionName' implicitly has an 'any' type.
      async (collectionName) => {
        const coll = collections.find((c) => c.pluralName === collectionName);
        if (!coll) return;
        const fields = mapValues(initializeField)(supportedFieldsBySlug(coll));
        const newExpression = ECall({
          fun: Expressions.qualifiedVariable(
            '__SitePlugin',
            `Create ${collectionName}`
          ),
          arg: ERecord({
            'Item Status': EHole(),
            'Basic Info Fields': EHole(),
            ...(Object.keys(fields).length === 0
              ? null
              : {
                  'Custom Fields': ERecord(fields),
                }),
          }),
        });
        if (hasDependencies) {
          const response = await confirm({
            title: 'Change Collection?',
            content:
              "You're currently using data from the current collection in other parts of the flow. Changing a collection might break the flow.",
            submit: {label: 'Change Collection', intent: 'primary'},
            cancel: {label: 'Cancel', intent: 'default'},
          });
          if (response.type === 'confirm') {
            dispatch(updateExpressionAtAction(newExpression, []));
            return;
          } else {
            return;
          }
        }
        dispatch(updateExpressionAtAction(newExpression, []));
      },
      [dispatch, hasDependencies, collections]
    );

    return (
      <>
        <CollectionSettings
          onChange={onCollectionChange}
          value={name}
          collections={collections}
          blockType="Create"
        />
        {call ? (
          <ExpressionEditor
            expression={call.arg}
            dispatch={dispatchAtArg(dispatch)}
          />
        ) : null}
      </>
    );
  },
});
